// Catalog.ID Client — Extended Supersecret Format
// ============================================================================
// Generates and validates the extended supersecret: 4 lines of 12 characters
// (11 random + 1 checksum per line), drawn from the BASE57 alphabet.
// See Recovery Kit Addendum §2.5 for the authoritative specification.

import { sha256 } from '@noble/hashes/sha2.js';
import { encodeUtf8 } from './aes';

export const BASE57_ALPHABET = 'abcdefghjkmnopqrstuvwxyzABCDEFGHIJKLMNPQRSTUVWXYZ23456789';

const LINES = 4;
const RANDOM_PER_LINE = 11;
const CHARS_PER_LINE = 12;

export function generateSupersecret(): string {
    return generateSupersecretFrom(() => crypto.getRandomValues(new Uint8Array(1))[0]);
}

/**
 * Generate from an arbitrary byte source — the entropy-ceremony path
 * (crypto/entropy.ts) feeds a seeded stream through the SAME rejection
 * sampler, so plain and ceremony generation share one distribution.
 */
export function generateSupersecretFrom(nextByte: () => number): string {
    const limit = 256 - (256 % BASE57_ALPHABET.length);
    let random44 = '';
    for (let i = 0; i < LINES * RANDOM_PER_LINE; i++) {
        let r: number;
        do {
            r = nextByte();
        } while (r >= limit);
        random44 += BASE57_ALPHABET[r % BASE57_ALPHABET.length];
    }
    return assembleSupersecret(random44);
}

/**
 * Rebuild the 4-line display form from the 44 random characters: recompute
 * each line's checksum and insert the dash grouping. Inverse of
 * {@link parseSupersecret}'s `random44`; used by Recovery Sheet reconstruction
 * (Addendum §4.1) as well as generation.
 */
export function assembleSupersecret(random44: string): string {
    if (random44.length !== LINES * RANDOM_PER_LINE) {
        throw new Error(`Expected ${LINES * RANDOM_PER_LINE} random characters, got ${random44.length}`);
    }
    const lines: string[] = [];
    for (let line = 0; line < LINES; line++) {
        const random = random44.slice(line * RANDOM_PER_LINE, (line + 1) * RANDOM_PER_LINE);
        const checksum = computeLineChecksum((line + 1) as 1 | 2 | 3 | 4, random);
        const full = random + checksum;
        lines.push(`${full.slice(0, 4)}-${full.slice(4, 8)}-${full.slice(8, 12)}`);
    }
    return lines.join('\n');
}

export function computeLineChecksum(lineNumber: 1 | 2 | 3 | 4, randomChars: string): string {
    const input =
        'Catalog.ID supersecret v2 line checksum' +
        '1' +
        'base57-cl-v1' +
        lineNumber.toString() +
        randomChars;
    const hash = sha256(encodeUtf8(input));
    const value = bigEndianMod(hash, BASE57_ALPHABET.length);
    return BASE57_ALPHABET[value];
}

export function parseSupersecret(input: string): { random44: string; lineErrors: number[] } {
    const cleaned = input.replace(/[-\s]/g, '');
    if (cleaned.length !== LINES * CHARS_PER_LINE) {
        return { random44: '', lineErrors: [1, 2, 3, 4] };
    }

    const lineErrors: number[] = [];
    let random44 = '';
    for (let i = 0; i < LINES; i++) {
        const lineChars = cleaned.slice(i * CHARS_PER_LINE, (i + 1) * CHARS_PER_LINE);
        const random = lineChars.slice(0, RANDOM_PER_LINE);
        const checksum = lineChars[RANDOM_PER_LINE];
        const expected = computeLineChecksum((i + 1) as 1 | 2 | 3 | 4, random);
        if (checksum !== expected) {
            lineErrors.push(i + 1);
        }
        random44 += random;
    }
    return { random44, lineErrors };
}

export function extractRandomBytes(input: string): Uint8Array {
    const { random44, lineErrors } = parseSupersecret(input);
    if (lineErrors.length > 0) {
        throw new Error(`Supersecret checksum failed on line(s): ${lineErrors.join(', ')}`);
    }
    return encodeUtf8(random44);
}

function bigEndianMod(hash: Uint8Array, mod: number): number {
    let result = 0;
    for (let i = 0; i < hash.length; i++) {
        result = (result * 256 + hash[i]) % mod;
    }
    return result;
}
